// ── Default country / dial code (client single point) ──────────────────────────────────────────────
// Mirror of server/src/services/dialcode.ts. /api/me carries `dialDefaults` for the ACTIVE company (its
// own region when set, else the workspace's): the phone placeholder and the nationality a new person
// starts with. Pages without /api/me (portal, share pages) fall back to the region's bare dial code.
// Nothing here rewrites a stored phone number — a hint is a placeholder, a default is only a starting value.
import { useMemo } from 'react';
import { useStore } from '../store';
import { region } from './region';

export type DialDefaults = {
  country: string;      // ISO 3166-1 alpha-2 ('' = not set)
  countryName: string;  // the country pack's name ('' = unknown country)
  dialCode: string;     // e.g. +66 ('' = not set)
  phoneSample: string;  // national part of an example number ('' = none known)
  phoneHint: string;    // placeholder for a phone input ('' = none)
  nationality: string;  // default nationality word ('' = none)
};
type MeWithDial = { dialDefaults?: Partial<DialDefaults> | null; region?: { country?: string; dialCode?: string } | null } | null | undefined;

const s = (v: unknown): string => String(v ?? '').trim();
const normalise = (raw: Partial<DialDefaults> | null | undefined, fallback: { country: string; dialCode: string }): DialDefaults => {
  if (!raw) return { country: fallback.country, countryName: '', dialCode: fallback.dialCode, phoneSample: '', phoneHint: fallback.dialCode, nationality: '' };
  return {
    country: s(raw.country).toUpperCase(), countryName: s(raw.countryName), dialCode: s(raw.dialCode),
    phoneSample: s(raw.phoneSample), phoneHint: s(raw.phoneHint), nationality: s(raw.nationality),
  };
};

/** The defaults right now (non-React callers). */
export function dialDefaults(): DialDefaults {
  let me: MeWithDial;
  try { me = useStore.getState().me as unknown as MeWithDial; } catch { me = null; }
  const r = region();
  return normalise(me?.dialDefaults, { country: r.country, dialCode: r.dialCode });
}
/** Hook form: re-renders when /api/me (or the active company) changes. */
export function useDialDefaults(): DialDefaults {
  const raw = useStore(st => (st.me as unknown as MeWithDial)?.dialDefaults);
  const reg = useStore(st => st.me?.region);
  return useMemo(() => {
    const r = region();
    return normalise(raw, { country: r.country, dialCode: r.dialCode });
  }, [raw, reg]); // eslint-disable-line react-hooks/exhaustive-deps
}
